//
import useAppStore from "@/context/store";
import { useSafeState } from "ahooks";

// 存储所有实例的销毁方法
const modalList = [];

/**
 * 弹框封装个静态方法，能够使用 open(props) 方法直接打开弹框
 * @template T
 * @param {(...args: T)=>JSX.Element} Modal
 * @returns {{
 * open: (...args: T&{visibleControl?: boolean} | Object) => {
 *  destroy: function
 *  update: function
 * },
 *  asyncOpen: (...args: T&{visibleControl: boolean} | Object)=> Promise<any>
 * }}
 */
export default function ModalWrap(Modal) {
  // 避免重复封装
  if (Modal.open) {
    return Modal;
  }

  /**
   * 打开弹框通用方法
   * @param {*} props 弹框所需参数
   * @param {*} props.visibleControl 自主控制弹框显示隐藏
   * @returns
   */
  function open(props = {}) {
    const name = props.name;
    // 手动控制弹框显示隐藏，使用场景： 当onOk与onCancel不需要关闭弹框时，可以使用visibleControl
    var visibleControl = props.visibleControl;

    const controlFn = {
      name,
    };

    function WrapComponent() {
      const [visible, setVisible] = useSafeState(true);
      const [modalProps, setProps] = useSafeState({
        destroyOnClose: true,
        ...props,
        onCancel(data) {
          if (typeof props.onCancel === "function") {
            props.onCancel(data);
          }

          if (!visibleControl) {
            setVisible(false);
          }
        },
        onOk(...args) {
          if (typeof props.onOk === "function") {
            props.onOk(...args);
          }
          if (!visibleControl) {
            setVisible(false);
          }
        },
        afterClose() {
          if (typeof props.afterClose === "function") {
            props.afterClose();
          }
          setTimeout(() => {
            // 在关闭后进行销毁弹框
            useAppStore.setState(({ dialogs }) => ({
              dialogs: dialogs.filter((item) => item !== instance),
            }));
            modalList.splice(modalList.indexOf(controlFn), 1);
          }, 0);
        },
      });

      controlFn.destroy = controlFn.close = function () {
        setVisible(false);
      };

      controlFn.update = function (configUpdate) {
        if (typeof configUpdate === "function") {
          setProps(configUpdate(modalProps));
        } else {
          setProps({
            ...modalProps,
            ...configUpdate,
            onCancel(data) {
              if (typeof configUpdate.onCancel === "function") {
                configUpdate.onCancel(data);
              }

              if (!visibleControl) {
                setVisible(false);
              }
            },
            onOk(...args) {
              if (typeof configUpdate.onOk === "function") {
                configUpdate.onOk(...args);
              }
              if (!visibleControl) {
                setVisible(false);
              }
            },
            afterClose() {
              if (typeof configUpdate.afterClose === "function") {
                configUpdate.afterClose();
              }
              // 在关闭后进行销毁弹框
              setTimeout(() => {
                // 在关闭后进行销毁弹框
                useAppStore.setState(({ dialogs }) => ({
                  dialogs: dialogs.filter((item) => item !== instance),
                }));

                modalList.splice(modalList.indexOf(controlFn), 1);
              }, 0);
            },
          });
        }
      };

      return <Modal {...modalProps} visible={visible} />;
    }

    const instance = (
      <WrapComponent key={useAppStore.getState().dialogs.length} />
    );

    controlFn.instance = instance;
    useAppStore.setState(({ dialogs }) => ({
      dialogs: [...dialogs, instance],
    }));

    // 将销毁方法存储起来,为后续销毁所有弹框使用
    modalList.push(controlFn);

    return controlFn;
  }
  // 可以使用 await 来等待弹框结束
  function asyncOpen(props = {}) {
    return new Promise((resolve, reject) => {
      open({
        ...props,
        onOk(data) {
          if (typeof props.onOk === "function") {
            props.onOk(data);
          }
          resolve(data);
        },
        onCancel(data) {
          if (typeof props.onCancel === "function") {
            props.onCancel(data);
          }
          reject(data);
        },
      });
    });
  }
  Modal.open = open;
  Modal.asyncOpen = asyncOpen;

  return Modal;
}

// 获取所有弹框实例，可用来操控弹框的更新与关闭
export function getModalList() {
  return modalList;
}
/**
 * 销毁所有由open打开的弹框
 */
export function destroyAll() {
  while (modalList.length) {
    var { close } = modalList.pop();

    if (close) {
      close();
    }
  }
}

export function destroyByInstance(ins) {
  useAppStore.setState(({ dialogs }) => ({
    dialogs: dialogs.filter((item) => item !== ins),
  }));
}
